// src/pages/Catalog/components/ProductModal.js
import React, { useState, useEffect } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Grid,
    IconButton,
    Typography,
    Box,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import ImageIcon from "@material-ui/icons/Image";
import CloseIcon from "@material-ui/icons/Close";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
const useStyles = makeStyles((theme) => ({
    content: {
        paddingTop: theme.spacing(2),
    },
    imagePreview: {
        width: "100%",
        height: 200,
        objectFit: "contain",
        marginTop: theme.spacing(1),
        borderRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.grey[100],
    },
    imageContainer: {
        position: "relative",
        marginBottom: theme.spacing(2),
    },
    deleteButton: {
        position: "absolute",
        top: theme.spacing(1),
        right: theme.spacing(1),
        backgroundColor: theme.palette.background.paper,
    },
    uploadButton: {
        width: "100%",
        height: 200,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        border: `2px dashed ${theme.palette.grey[300]}`,
        borderRadius: theme.shape.borderRadius,
        cursor: "pointer",
        "&:hover": {
            backgroundColor: theme.palette.grey[50],
        },
    },
    attributeSection: {
        marginTop: theme.spacing(2),
    },
    attributeChips: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(1),
        marginTop: theme.spacing(1),
    },
}));

const currencies = [
    { value: "USD", label: "USD - Dólar estadounidense" },
    { value: "EUR", label: "EUR - Euro" },
    { value: "COR", label: "COR - Córdoba Nicaragüense" }
    // Añade más monedas según necesites
];

const ProductModal = ({ open, onClose, productId, onProduct }) => {
    const classes = useStyles();
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [product, setProduct] = useState({
        name: "",
        description: "",
        price: "",
        currency: "USD",
        images: [],
        attributes: [],
    });
    const [newAttribute, setNewAttribute] = useState({ name: "", value: "" });


    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const { data } = await api.get("/categories");
                setCategories(data);
            } catch (err) {
                toastError(err);
            }
        };

        if (open) {
            fetchCategories();
        }
    }, [open]);

    useEffect(() => {
        const fetchProduct = async () => {
            if (!productId) return;
            try {
                const { data } = await api.get(`/products/${productId}`);
                setProduct(data);
            } catch (err) {
                toastError(err);
            }
        };
        fetchProduct();
    }, [productId]);

    useEffect(() => {
        if (!open) {
            setProduct({
                name: "",
                description: "",
                price: "",
                currency: "USD",
                images: [],
                attributes: [],
            });
            setNewAttribute({ name: "", value: "" });
        }
    }, [open]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const formData = new FormData();
        formData.append("name", product.name);
        formData.append("description", product.description || "");
        formData.append("price", product.price);
        formData.append("currency", product.currency);
        formData.append("categoryId", product.categoryId || "");

        if (product.attributes && Array.isArray(product.attributes)) {
            formData.append("attributes", JSON.stringify(product.attributes));
        } else {
            formData.append("attributes", JSON.stringify([]));
        }

        if (product.newImages && product.newImages.length > 0) {
            product.newImages.forEach(file => {
                formData.append("images", file);
            });
        }

        try {
            const { data } = productId
                ? await api.put(`/products/${productId}`, formData)
                : await api.post("/products", formData);

            onProduct(data);
            toast.success(productId ? "Producto actualizado con éxito" : "Producto creado con éxito");
            onClose(); // Close modal after success
        } catch (err) {
            toastError(err);
        }
        setLoading(false);
    };

    const handleDeleteImage = async (imageId, index, isNewImage = false) => {
        try {
            if (isNewImage) {
                setProduct((prev) => ({
                    ...prev,
                    newImages: prev.newImages.filter((_, i) => i !== index)
                }));
                return;
            }

            const updatedData = {
                ...product,
                imagesToDelete: [imageId]
            };

            const { data } = await api.put(`/products/${productId}`, updatedData);

            setProduct(data);
            toast.success("Imagen eliminada con éxito");
        } catch (err) {
            toastError(err);
        }
    };

    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files);
        setProduct((prev) => ({
            ...prev,
            newImages: [...(prev.newImages || []), ...files],
        }));
    };

    const handleAddAttribute = () => {
        if (newAttribute.name && newAttribute.value) {
            setProduct((prev) => ({
                ...prev,
                attributes: [...prev.attributes, newAttribute],
            }));
            setNewAttribute({ name: "", value: "" });
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            aria-labelledby="form-dialog-title"
        >
            <DialogTitle id="form-dialog-title">
                {productId ? "Editar Producto" : "Nuevo Producto"}
            </DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent className={classes.content}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                label="Nombre del producto"
                                fullWidth
                                required
                                value={product.name}
                                onChange={(e) =>
                                    setProduct({ ...product, name: e.target.value })
                                }
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                label="Descripción"
                                fullWidth
                                multiline
                                rows={4}
                                value={product.description}
                                onChange={(e) =>
                                    setProduct({ ...product, description: e.target.value })
                                }
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <TextField
                                label="Precio"
                                fullWidth
                                required
                                type="number"
                                value={product.price}
                                onChange={(e) =>
                                    setProduct({ ...product, price: e.target.value })
                                }
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <FormControl fullWidth>
                                <InputLabel>Moneda</InputLabel>
                                <Select
                                    value={product.currency}
                                    onChange={(e) =>
                                        setProduct({ ...product, currency: e.target.value })
                                    }
                                >
                                    {currencies.map((currency) => (
                                        <MenuItem key={currency.value} value={currency.value}>
                                            {currency.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <FormControl fullWidth variant="outlined" margin="dense">
                                <InputLabel>{i18n.t("products.form.category", "Categoría")}</InputLabel>
                                <Select
                                    value={product.categoryId || ""}
                                    onChange={(e) => setProduct({ ...product, categoryId: e.target.value })}
                                    label={i18n.t("products.form.category", "Categoría")}
                                >
                                    <MenuItem value="">
                                        <em>{i18n.t("products.form.noCategory", "Sin categoría")}</em>
                                    </MenuItem>
                                    {categories.map((category) => (
                                        <MenuItem key={category.id} value={category.id}>
                                            {category.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="subtitle1">Imágenes</Typography>
                            <input
                                accept="image/*"
                                style={{ display: "none" }}
                                id="raised-button-file"
                                multiple
                                type="file"
                                onChange={handleImageUpload}
                            />
                            <label htmlFor="raised-button-file">
                                <div className={classes.uploadButton}>
                                    <ImageIcon fontSize="large" color="action" />
                                    <Typography variant="body2" color="textSecondary">
                                        Haz clic para subir imágenes
                                    </Typography>
                                </div>
                            </label>

                            <Grid container spacing={2} style={{ marginTop: 16 }}>
                                {product.images?.map((image, index) => (
                                    <Grid item xs={4} key={image.id || index}>
                                        <div className={classes.imageContainer}>
                                            <img
                                                src={`${process.env.REACT_APP_BACKEND_URL}public/products/${image.imageUrl}`}
                                                alt={`Product ${index + 1}`}
                                                className={classes.imagePreview}
                                            />
                                            <IconButton
                                                className={classes.deleteButton}
                                                size="small"
                                                onClick={() => handleDeleteImage(image.id, index)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </div>
                                    </Grid>
                                ))}
                                {product.newImages?.map((image, index) => (
                                    <Grid item xs={4} key={`new-${index}`}>
                                        <div className={classes.imageContainer}>
                                            <img
                                                src={URL.createObjectURL(image)}
                                                alt={`New ${index + 1}`}
                                                className={classes.imagePreview}
                                            />
                                            <IconButton
                                                className={classes.deleteButton}
                                                size="small"
                                                onClick={() => handleDeleteImage(null, index, true)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </div>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>

                        <Grid item xs={12} className={classes.attributeSection}>
                            <Typography variant="subtitle1">Atributos</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={5}>
                                    <TextField
                                        fullWidth
                                        label="Nombre del atributo"
                                        value={newAttribute.name}
                                        onChange={(e) =>
                                            setNewAttribute({ ...newAttribute, name: e.target.value })
                                        }
                                    />
                                </Grid>
                                <Grid item xs={5}>
                                    <TextField
                                        fullWidth
                                        label="Valor"
                                        value={newAttribute.value}
                                        onChange={(e) =>
                                            setNewAttribute({ ...newAttribute, value: e.target.value })
                                        }
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        color="primary"
                                        onClick={handleAddAttribute}
                                        style={{ height: '100%' }}
                                    >
                                        <AddIcon />
                                    </Button>
                                </Grid>
                            </Grid>
                            <Box className={classes.attributeChips}>
                                {product.attributes?.map((attr, index) => (
                                    <Chip
                                        key={index}
                                        label={`${attr.name}: ${attr.value}`}
                                        onDelete={() => {
                                            setProduct((prev) => ({
                                                ...prev,
                                                attributes: prev.attributes.filter((_, i) => i !== index),
                                            }));
                                        }}
                                    />
                                ))}
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} color="default">
                        Cancelar
                    </Button>
                    <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                        disabled={loading}
                    >
                        {loading
                            ? "Guardando..."
                            : productId
                                ? "Actualizar"
                                : "Crear"}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default ProductModal;